import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlag } from '../constants/featureFlags';
import { useToast } from '../hooks/useToastContext';
import {
  resyncWorksheetGoals,
  getRogerDocClinics,
} from '../services/rogerDocApi';
import GoalsSection from '../components/worksheetGoals/GoalsSection';

const WorksheetGoals: React.FC = () => {
  const { hasFeatureFlag, user } = useStore(
    useShallow(state => ({
      hasFeatureFlag: state.hasFeatureFlag,
      user: state.user,
    }))
  );
  const [selectedClinicKey, setSelectedClinicKey] = useState<string | null>(
    null
  );
  const [agencyClinicKeys, setAgencyClinicKeys] = useState<
    { value: string; label: string }[]
  >([]);
  const [isResyncing, setIsResyncing] = useState(false);
  const [isLoadingClinics, setIsLoadingClinics] = useState(true);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const { success, error } = useToast();

  if (!hasFeatureFlag(FeatureFlag.ADMIN_WORKSHEET_GOALS)) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex items-center gap-2'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don't have permission to access Worksheet Goals features. Please
            contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Get clinic keys for the current agency
  useEffect(() => {
    const fetchClinics = async () => {
      if (!user?.agency?.id) {
        setAgencyClinicKeys([]);
        console.log('No agency found');
        return;
      }

      try {
        const clinics = await getRogerDocClinics(user.agency.system_name);
        const formattedClinics = clinics.map(clinic => ({
          value: clinic.id,
          label: clinic.name,
        }));
        setAgencyClinicKeys(formattedClinics);
        console.log('Formatted clinics:', formattedClinics);
        // Set initial clinic key if not set and we have clinics
        if (formattedClinics.length > 0 && !selectedClinicKey) {
          setSelectedClinicKey(formattedClinics[0].value);
        }
      } catch (err) {
        console.error('Failed to fetch clinics:', err);
        error('Error', 'Failed to load clinics');
      } finally {
        setIsLoadingClinics(false);
      }
    };

    fetchClinics();
  }, [user?.agency?.id, selectedClinicKey]);

  // Handle resync button click
  const handleResync = async () => {
    if (!user?.agency?.system_name) {
      error(
        'Resync Failed',
        'No agency found. Please ensure you are logged in.'
      );
      return;
    }

    setIsResyncing(true);
    try {
      await resyncWorksheetGoals();
      setRefetchTrigger(prev => prev + 1);
      success('Sync Complete', 'Goals synced successfully from Wellsky!');
    } catch (err) {
      console.error('Failed to resync worksheet goals:', err);
      error('Sync Failed', 'Failed to sync goals. Please try again.');
    } finally {
      setIsResyncing(false);
    }
  };

  // Prepare options for the dropdown
  const clinicKeyOptions = [
    ...agencyClinicKeys,
    {
      value: 'all_clinics',
      label: 'All Clinics',
    },
  ];

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-start'>
        <div>
          <h1 className='text-2xl font-bold'>Goals and Interventions</h1>
          <p className='text-muted-foreground text-sm mt-1'>
            You can review the goals from your Wellsky library and select which
            ones should be required to be automatically added for{' '}
            <strong>all users and all patients</strong> of that speciality
          </p>
        </div>
      </div>

      {/* Goals Section */}
      <GoalsSection
        clinicKey={selectedClinicKey}
        onClinicKeyChange={setSelectedClinicKey}
        clinicKeyOptions={clinicKeyOptions}
        showClinicSelector={agencyClinicKeys.length > 1}
        agencyName={user?.agency?.written_name || user?.agency?.system_name}
        onResync={handleResync}
        isResyncing={isResyncing}
        isLoadingClinics={isLoadingClinics}
        canResync={!!user?.agency?.system_name}
        refetchTrigger={refetchTrigger}
      />
    </div>
  );
};

export default WorksheetGoals;
